import { cn } from "../../lib/utils";

/**
 * Countdown state, driven by the derived congress phase:
 * - `pre`: days/hours/minutes until the congress starts (shown as chips)
 * - `live`: the congress is running (shown as a "live" pill, uppercased by CSS)
 * - `post`: the congress has ended (no countdown)
 */
type CongressCountdown =
  | { phase: "pre"; days: number; hours: number; minutes: number }
  | { phase: "live" }
  | { phase: "post" };

/**
 * The countdown's words, already resolved in the CONTENT language by
 * `resolveCongressCountdownCopy` — the unit letters appended to the digits and
 * the text of the "live" pill.
 *
 * Passed in rather than translated here: this card is a design-system primitive
 * rendered in Storybook, which mounts no i18n provider, so a `t()` call inside
 * it would throw there. It also keeps the language axis a decision the calling
 * surface makes visibly, never one this component takes silently.
 */
type CongressCountdownCopy = {
  daysUnit: string;
  hoursUnit: string;
  minutesUnit: string;
  liveNow: string;
};

/**
 * What the banner draws over the cover colour (`CongressSpace.coverType`,
 * resolved server-side against the images actually uploaded):
 * - `color`: the colour itself, with an optional logotype
 * - `backdrop`: a decorative image the card still titles itself over
 * - `artwork`: a self-contained congress banner — it already carries the name,
 *   the dates and the logotype, so it has NOWHERE to put ours. Only the
 *   countdown, which the image cannot know, goes on top.
 */
type CongressCover =
  | { type: "color"; logoUrl: string | null }
  | { type: "backdrop"; backdropUrl: string; logoUrl: string | null }
  | { type: "artwork"; artworkUrl: string };

/**
 * - `full`: cover banner (Discovery hero), driven by the `cover` shape
 * - `min`: compact horizontal row, colour only (Resources section header) —
 *   it renders no imagery at all, hence no cover to carry
 */
type CongressCardDisplay =
  { variant: "min" } | { variant: "full"; cover: CongressCover };

/**
 * Admin-controlled text tone (`CongressSpace.coverTextMode`):
 * - `auto`: derived from the cover colour's WCAG luminance
 * - `light` / `dark`: explicit override
 */
type CongressCoverTextMode = "auto" | "light" | "dark";

type CongressCardProps = {
  /** Congress name, primary title (full: long name, min: short name) */
  name: string;
  /** Date + location line, e.g. "5-9 September 2026 | Athens, Greece" */
  dateLabel: string;
  /** Cover background colour, used as the fallback behind (or instead of) the image */
  coverColor: string;
  /** Admin's text-tone choice for this cover */
  coverTextMode: CongressCoverTextMode;
  /** Derived countdown state */
  countdown: CongressCountdown;
  /** Countdown wording, resolved in the content language by the caller */
  countdownCopy: CongressCountdownCopy;
  /** Layout, and for the full banner what it draws over the colour */
  display: CongressCardDisplay;
  /** Click handler; when set the card renders as a button */
  onClick?: () => void;
  /** Additional class names merged onto the root element */
  className?: string;
};

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/**
 * Whether white text reads on the cover colour, when the admin left the mode on
 * `auto`. White text needs ≥ 4.5:1, i.e. WCAG relative luminance ≤ 0.1833.
 * Unparseable colours default to light.
 *
 * Artwork is never measured because text is never drawn over it — a cover image
 * carries its own title.
 */
function isCoverTextLightAuto(coverColor: string): boolean {
  const match = /^#([0-9a-f]{6})$/i.exec(coverColor);
  if (match === null) return true;
  const hex = match[1];
  const channel = (offset: number): number => {
    const c = Number.parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const luminance =
    0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  return luminance <= 0.1833;
}

/**
 * Chip fill depth. A chip is always a dark pill with white digits — only how
 * deep it fills follows what it sits on:
 * - `soft`: over a dark backing — a dark cover colour, or the black scrim a
 *   light-text backdrop already paints — which separates the chip for it
 * - `solid`: over a light backing (white-on-white otherwise) or over bare
 *   imagery with no scrim at all, where the chip separates itself
 */
type CongressChipFill = "soft" | "solid";

function CongressCountdownChips({
  countdown,
  countdownCopy,
  chipFill,
}: {
  countdown: CongressCountdown;
  countdownCopy: CongressCountdownCopy;
  chipFill: CongressChipFill;
}): React.JSX.Element | null {
  if (countdown.phase === "pre") {
    return (
      <div className="flex items-center gap-1.5">
        {/* Keyed on the unit slot, not on the rendered text: two units can
            render the same string in some locale, and a duplicate React key
            would be a silent rendering defect rather than a visible one. */}
        {[
          {
            slot: "days",
            text: `${pad(countdown.days)}${countdownCopy.daysUnit}`,
          },
          {
            slot: "hours",
            text: `${pad(countdown.hours)}${countdownCopy.hoursUnit}`,
          },
          {
            slot: "minutes",
            text: `${pad(countdown.minutes)}${countdownCopy.minutesUnit}`,
          },
        ].map((chip) => (
          <span
            key={chip.slot}
            className={cn(
              "rounded-md px-2 py-1 text-sm font-medium leading-none text-white",
              chipFill === "soft" ? "bg-black/40" : "bg-ds-slate-950/80"
            )}
          >
            {chip.text}
          </span>
        ))}
      </div>
    );
  }
  if (countdown.phase === "live") {
    return (
      <span className="inline-flex w-fit items-center gap-1 rounded-md bg-ds-strawberry-600 px-2 py-1 text-xs font-semibold uppercase leading-none text-white">
        <span className="size-1.5 rounded-full bg-white" />
        {countdownCopy.liveNow}
      </span>
    );
  }
  return null;
}

function CongressCard({
  name,
  dateLabel,
  coverColor,
  coverTextMode,
  countdown,
  countdownCopy,
  display,
  onClick,
  className,
}: CongressCardProps): React.JSX.Element {
  const Wrapper = onClick === undefined ? "div" : "button";
  const buttonType = onClick === undefined ? undefined : "button";
  const interactive = onClick !== undefined;
  const textLight =
    coverTextMode === "auto"
      ? isCoverTextLightAuto(coverColor)
      : coverTextMode === "light";

  if (display.variant === "min") {
    return (
      <Wrapper
        type={buttonType}
        onClick={onClick}
        style={{ backgroundColor: coverColor }}
        className={cn(
          "flex w-full items-center justify-between gap-3 rounded-xl px-3.5 py-2.5 text-left font-dm-sans",
          textLight ? "text-white" : "text-ds-black",
          interactive && "cursor-pointer",
          className
        )}
      >
        <div className="flex min-w-0 flex-col">
          <p className="m-0 truncate text-sm/5 font-semibold ">{name}</p>
          <p
            className={cn(
              "m-0 truncate text-xs",
              textLight ? "text-white/85" : "text-ds-black/85"
            )}
          >
            {dateLabel}
          </p>
        </div>
        <div className="shrink-0">
          <CongressCountdownChips
            countdown={countdown}
            countdownCopy={countdownCopy}
            chipFill={textLight ? "soft" : "solid"}
          />
        </div>
      </Wrapper>
    );
  }

  const cover = display.cover;
  const backgroundUrl =
    cover.type === "artwork"
      ? cover.artworkUrl
      : cover.type === "backdrop"
        ? cover.backdropUrl
        : null;

  return (
    <Wrapper
      type={buttonType}
      onClick={onClick}
      style={{ backgroundColor: coverColor }}
      className={cn(
        "relative grid w-full grid-cols-1 overflow-hidden rounded-xl text-left font-dm-sans",
        interactive && "cursor-pointer",
        className
      )}
    >
      {/* The maquette's 11:5 banner ratio is a floor, not a fixed height. This
          spacer shares the single grid cell with the content, so a card whose
          content is taller than the ratio (logo + two-line name + countdown)
          grows instead of clipping the countdown off its bottom edge. */}
      <div className="col-start-1 row-start-1 aspect-11/5 w-full self-start" />

      {backgroundUrl !== null && (
        <img
          src={backgroundUrl}
          alt=""
          className="pointer-events-none absolute inset-0 size-full object-cover"
        />
      )}

      {/* An artwork cover already carries the congress logotype, name and dates
          in the image itself, so drawing ours on top double-prints the banner.
          Only the countdown — which the artwork cannot know — stays, and with
          no text of ours to protect the scrim goes with it (the chips are
          opaque pills that read on their own). */}
      {cover.type === "artwork" ? (
        <div className="relative col-start-1 row-start-1 flex flex-col justify-end px-3.5 pb-3 pt-8">
          <CongressCountdownChips
            countdown={countdown}
            countdownCopy={countdownCopy}
            chipFill="solid"
          />
        </div>
      ) : (
        <div className="relative col-start-1 row-start-1 flex flex-col justify-between">
          <div className="flex items-start justify-end px-3.5 pt-3">
            {cover.logoUrl !== null && (
              <img
                src={cover.logoUrl}
                alt=""
                className="h-8 w-auto object-contain"
              />
            )}
          </div>

          <div
            className={cn(
              "flex flex-col gap-1.5 px-3.5 pb-3 pt-8",
              // A backdrop is a decorative image the card titles itself over,
              // so the title needs separating from it. The scrim is black, so
              // it only serves white text — under dark text it would darken
              // exactly what has to stay light, and the tone carries the
              // contrast alone (a colour cover needs no scrim at all).
              cover.type === "backdrop" &&
                textLight &&
                "bg-linear-to-b from-transparent to-black/55"
            )}
          >
            <p
              className={cn(
                "m-0 line-clamp-2 text-base/5 font-bold",
                textLight ? "text-white" : "text-ds-black"
              )}
            >
              {name}
            </p>
            <p
              className={cn(
                "m-0 truncate text-xs/4 font-medium",
                textLight ? "text-white/85" : "text-ds-black/85"
              )}
            >
              {dateLabel}
            </p>
            <CongressCountdownChips
              countdown={countdown}
              countdownCopy={countdownCopy}
              chipFill={textLight ? "soft" : "solid"}
            />
          </div>
        </div>
      )}
    </Wrapper>
  );
}

export {
  CongressCard,
  type CongressCardProps,
  type CongressCountdown,
  type CongressCountdownCopy,
};
